/**
 * File a process request FOR a worker, from the admin side.
 *
 * Owner 2026-09-05: 在 admin 的 process 的模块中，是否支持 admin 创建 request？当前没有这个按钮和路径 … 目前
 * attendance 中 admin 可以自己创建一些补卡等一系列的操作.
 *
 * ── WHY THIS IS A THIN SHELL AND NOT A FORM ─────────────────────────────────────────────────────
 *
 * The server validates the submitted `data` against the definition's form schema, so an admin dialog with
 * its own fields would 400 on any definition with a required field — and a picker whose options are
 * refused is worse than no picker (svdy-ui §19). So the form is not reimplemented: this picks the SUBJECT
 * and then renders the very same `WorkerSubmitRequest`, handed an on-behalf data source.
 *
 * That data source is what makes the reuse correct rather than merely convenient (see
 * `submitRequestDataSource.ts`): the process picker lists what the TARGET may start, the auto-filled name
 * is the requester's rather than the admin's, the leave span is measured against the subject's calendar,
 * and the POST goes to the admin route which records `created_by` (alembic 120) and answers
 * `filed_on_behalf`.
 *
 * ── WHY THE ATTRIBUTION MATTERS ENOUGH TO HAVE BLOCKED THIS ─────────────────────────────────────
 *
 * `requester_id` carries INTENT — it is whose request it IS, and the approval history shows it — so filing
 * for someone without recording who typed it attributes an intent to a person who never expressed it. The
 * endpoint refused outright until the column existed. The banner below says so on screen, because an admin
 * about to type in someone else's name should see that the record will name them as the filer.
 */
import React, { useState } from 'react';
import {
  Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { organizationApi } from '../../../api';
import type { WorkerResponse } from '../../../api/organization';
import WorkerSubmitRequest from '../../WorkerPortal/WorkerSubmitRequest';
import { onBehalfRequestDataSource } from '../../WorkerPortal/submitRequestDataSource';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful submission so the list behind the dialog refreshes. */
  onFiled?: () => void;
}

const FileRequestForWorkerDialog: React.FC<Props> = ({ open, onClose, onFiled }) => {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState<WorkerResponse | null>(null);

  React.useEffect(() => {
    if (!open || workers.length) return;
    setLoading(true);
    organizationApi.workersApi.list('active')
      .then((r) => setWorkers(Array.isArray(r) ? r : []))
      .catch(() => setWorkers([]))
      .finally(() => setLoading(false));
  }, [open, workers.length]);

  const close = () => { setSubject(null); onClose(); };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
          {t('processes:requests.fileForWorker.title')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        <Stack spacing={2}>
          {/* Said on screen, not only in the audit trail: the record will name the admin as the filer and
              the worker as the requester, and the approver will see both. */}
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 1 }}>
            {t('processes:requests.fileForWorker.attribution')}
          </Alert>

          <Autocomplete
            options={workers}
            loading={loading}
            value={subject}
            onChange={(_, next) => setSubject(next)}
            getOptionLabel={(w) => `${w.first_name ?? ''} ${w.last_name ?? ''}`.trim()
              + (w.worker_code ? ` · ${w.worker_code}` : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                label={t('processes:requests.fileForWorker.subject')}
                helperText={t('processes:requests.fileForWorker.subjectHelp')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* The SAME form the worker portal uses. It is mounted only once a subject exists, because its
              process picker asks what THAT worker may start — mounting it earlier would fetch nothing and
              show an empty picker that looks broken. */}
          {subject ? (
            <WorkerSubmitRequest
              key={subject.id}
              source={onBehalfRequestDataSource(subject.id)}
              onFiled={() => { onFiled?.(); close(); }}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close} >
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileRequestForWorkerDialog;
